import { Box, TextField, Button, Menu, MenuItem } from "@mui/material";
import { useState } from "react";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

/**
 * DateFilter — reusable date-filter UI, matching the reference screenshot's
 * compact, professional style (calendar icon, consistent height/radius,
 * grouped as one visual unit rather than loose floating controls).
 *
 * IMPORTANT — this component is presentation-only for the From/To/Today/
 * All-Time part. It does not read `Date.now()`, does not call
 * `new Date(...)` on any value, and does not decide what counts as "today"
 * or how a range is interpreted for those. Every page using it keeps its
 * own existing `from`/`to` state and its own existing handlers — this
 * component just renders the inputs and forwards raw string values back
 * via the provided callbacks, exactly as the original inline
 * <TextField type="date"> elements did. This preserves each page's
 * existing date semantics (string comparison, no timezone conversion)
 * byte-for-byte; only the visual presentation changes.
 *
 * The one exception is the new "Last N days" preset menu below: since no
 * page previously had this feature, there is no existing per-page handler
 * to reuse for it. To guarantee it never disagrees with a page's own
 * "Today" button, it computes dates using the EXACT SAME method every
 * page's own "Today"/"All Time" logic already uses elsewhere in this
 * codebase — `new Date().toISOString().split("T")[0]` — and then calls the
 * same existing onFromChange/onToChange props. No page's own state or
 * handlers were changed to support this; it works everywhere DateFilter is
 * already used, automatically.
 *
 * Currently supports one variant — `mode="range"` — because every existing
 * date filter in this codebase (Dashboard, Candidates, JobRequirements,
 * ClientPositions, ClientCandidates) uses the same From/To + Today/All-Time
 * pattern. Additional modes (e.g. a preset dropdown) can be added later as
 * separate variants without touching this one, per the project's existing
 * pages not needing that pattern today.
 *
 * Props:
 *   from, to           - current raw string values (required), passed straight
 *                        through from the page's own existing state
 *   onFromChange       - (newValue: string) => void (required)
 *   onToChange         - (newValue: string) => void (required)
 *   isToday, isAllTime - booleans controlling which preset button is
 *                        highlighted (required) — computed by the page's
 *                        own existing logic (e.g. its own `dateMode()`),
 *                        not by this component
 *   onToday, onAllTime - () => void, called when a preset button is
 *                        clicked (required) — the page's own existing
 *                        handlers, unchanged
 *   size               - "small" (default) or "compact" for tighter layouts
 */
export default function DateFilter({
  from,
  to,
  onFromChange,
  onToChange,
  isToday,
  isAllTime,
  onToday,
  onAllTime,
  size = "small",
}) {
  const fieldWidth = size === "compact" ? 130 : 148;
  const [presetAnchor, setPresetAnchor] = useState(null);

  // Same exact string-generation method as every page's own "today" already
  // uses (see e.g. Dashboard.jsx / Candidates.jsx / JobRequirements.jsx:
  // `new Date().toISOString().split("T")[0]`) — kept identical on purpose
  // so "Last 7 days" can never disagree with what "Today" means on the same
  // page.
  const applyPreset = (days) => {
    const now = new Date();
    const fromD = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    onFromChange(fromD.toISOString().split("T")[0]);
    onToChange(now.toISOString().split("T")[0]);
    setPresetAnchor(null);
  };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        p: 0.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "8px",
        bgcolor: "background.paper",
      }}
    >
      <CalendarTodayOutlinedIcon
        sx={{ fontSize: 16, color: "text.secondary", ml: 0.5 }}
      />
      <TextField
        type="date"
        size="small"
        variant="standard"
        placeholder="From"
        InputLabelProps={{ shrink: true }}
        InputProps={{ disableUnderline: true }}
        value={from}
        onChange={(e) => onFromChange(e.target.value)}
        sx={{ width: fieldWidth, "& input": { fontSize: 13, py: 0.25 } }}
      />
      <Box sx={{ color: "text.secondary", fontSize: 12 }}>–</Box>
      <TextField
        type="date"
        size="small"
        variant="standard"
        placeholder="To"
        InputLabelProps={{ shrink: true }}
        InputProps={{ disableUnderline: true }}
        value={to}
        onChange={(e) => onToChange(e.target.value)}
        sx={{ width: fieldWidth, "& input": { fontSize: 13, py: 0.25 } }}
      />
      <Button
        size="small"
        variant={isToday ? "contained" : "text"}
        onClick={onToday}
        sx={{ minWidth: 0, px: 1.25, fontSize: 12.5, height: 28 }}
      >
        Today
      </Button>
      <Button
        size="small"
        variant={isAllTime ? "contained" : "text"}
        onClick={onAllTime}
        sx={{ minWidth: 0, px: 1.25, fontSize: 12.5, height: 28 }}
      >
        All Time
      </Button>
      <Button
        size="small"
        variant="text"
        endIcon={<ArrowDropDownIcon />}
        onClick={(e) => setPresetAnchor(e.currentTarget)}
        sx={{ minWidth: 0, px: 1.25, fontSize: 12.5, height: 28 }}
      >
        More
      </Button>
      <Menu
        anchorEl={presetAnchor}
        open={!!presetAnchor}
        onClose={() => setPresetAnchor(null)}
      >
        <MenuItem onClick={() => applyPreset(7)}>Last 7 days</MenuItem>
        <MenuItem onClick={() => applyPreset(15)}>Last 15 days</MenuItem>
        <MenuItem onClick={() => applyPreset(30)}>Last 30 days</MenuItem>
      </Menu>
    </Box>
  );
}
